import { useI18n } from 'vue-i18n'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getPartTitle } from '@/helpers/shared'
import type { ProjectImportIssue } from '@/helpers/project-transfer'
import type { PreparedProjectLoad, PreparedProjectLoadError } from '@/helpers/project-state-loader'

/** Human readable texts for project state analysis results (shared by modal and summary page). */
export function useProjectTransferTexts() {
  const { t } = useI18n()
  const { wt: wtGeneral } = useWidgetText('widget-texts-general')

  function roleLabel(role: string): string {
    return wtGeneral('roleParts', role) || role
  }

  function roleList(roles: string[] | undefined): string {
    return roles?.length ? roles.map(roleLabel).join(', ') : '–'
  }

  function partList(parts: string[] | undefined): string {
    return (parts ?? []).map((part) => getPartTitle(part, wtGeneral) || part).join(', ')
  }

  function countryLabel(country: string | null | undefined): string {
    if (!country) return '–'
    const key = `general.transfer.country.${country}`
    const label = t(key)
    return label === key ? country : label
  }

  function issueText(issue: ProjectImportIssue): string {
    switch (issue.code) {
      case 'country_mismatch':
        return t('general.transfer.import.errors.country_mismatch', {
          source: countryLabel(issue.sourceCountry),
          current: countryLabel(issue.currentCountry),
        })
      case 'no_matching_roles':
        return t('general.transfer.import.errors.no_matching_roles', {
          source: roleList(issue.sourceRoles),
          current: roleList(issue.currentRoles),
        })
      case 'roles_skipped':
      case 'roles_missing':
        return t(`general.transfer.import.warnings.${issue.code}`, { roles: roleList(issue.roles) })
      case 'incompatible_version':
        return t('general.transfer.import.errors.incompatible_version', { parts: partList(issue.parts) })
      default:
        return t(`general.transfer.import.warnings.${issue.code}`)
    }
  }

  function loadErrorText(error: PreparedProjectLoadError): string {
    switch (error.code) {
      case 'incompatible_version':
        return t('general.transfer.import.errors.incompatible_version', { parts: partList(error.parts) })
      case 'validation_failed':
        return t('general.transfer.import.errors.validation_failed', { message: (error.message ?? '').slice(0, 300) })
      default:
        return t(`general.transfer.import.errors.${error.code}`)
    }
  }

  /** All blocking reasons of a prepared load as one text (analysis errors or load error). */
  function preparedLoadErrorText(prepared: PreparedProjectLoad): string {
    if (prepared.error) return loadErrorText(prepared.error)
    return (prepared.analysis?.errors ?? []).map(issueText).join(' ')
  }

  return { roleLabel, roleList, partList, countryLabel, issueText, loadErrorText, preparedLoadErrorText }
}
